/**
 * PoliceDepartmentsListPage - Police departments list with infinite scroll
 *
 * Clean, minimal design with streamlined search and filter controls
 */

import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Skeleton } from '@/components/ui/skeleton';
import {
  PoliceDepartmentsFilter,
  useGetPoliceDepartmentsInfiniteInfiniteQuery,
} from '@/services/api/policeDepartmentsService';

import { PoliceDepartmentsTable } from './components/PoliceDepartmentsTable';

export const PoliceDepartmentsListPage = () => {
  const navigate = useNavigate();

  // Filter state
  const [searchQuery, setSearchQuery] = React.useState('');
  const [stateFilter, setStateFilter] = React.useState<string>('all');
  const [ordering, setOrdering] = React.useState<string>('-modified_at');

  // Debounce search query
  const [debouncedSearch, setDebouncedSearch] = React.useState(searchQuery);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Build query parameters
  const queryParams = useMemo<PoliceDepartmentsFilter>(
    () => ({
      search: debouncedSearch || undefined,
      state_code: stateFilter !== 'all' ? stateFilter : undefined,
      ordering,
    }),
    [debouncedSearch, stateFilter, ordering],
  );

  // Main data fetching with infinite scroll
  const {
    data: pages,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetPoliceDepartmentsInfiniteInfiniteQuery(queryParams, {
    initialPageParam: {
      offset: 0,
      limit: 100,
    },
  });

  // Flatten paginated data
  const departmentsData = useMemo(
    () => pages?.pages?.flatMap((page) => page.results) || [],
    [pages],
  );

  const totalCount = pages?.pages[0]?.count || 0;

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && departmentsData.length < totalCount) {
      fetchNextPage();
    }
  }, [fetchNextPage, isFetchingNextPage, departmentsData.length, totalCount]);

  const handleCreateClick = useCallback(() => {
    navigate('/policedepartments/create');
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="h-screen bg-gray-50 p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="space-y-2">
            {Array.from({ length: 12 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 p-8 flex flex-col min-h-0">
      <div className="mx-auto max-w-7xl w-full flex flex-col flex-1 min-h-0">
        <PoliceDepartmentsTable
          departments={departmentsData}
          totalCount={totalCount}
          onLoadMore={handleLoadMore}
          isLoadingNewFilter={isLoading && !isFetchingNextPage}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          stateFilter={stateFilter}
          onStateFilterChange={setStateFilter}
          onCreateClick={handleCreateClick}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      </div>
    </div>
  );
};
